import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  ListItem,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { djs } from '@duke-hq/djs';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

const PURGE_WINDOW_SECONDS = 60;

// Ticks once a second so the purge-rate-limit countdown stays live.
function useNowSeconds(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return Math.floor(now / 1000);
}

export function DangerZone({
  queued,
  generating,
  sqsVisible,
  lastFlush,
  sqsOk,
  onFlush,
  isFlushing,
}: {
  queued: number;
  generating: number;
  sqsVisible: number | null;
  lastFlush: { at: string; actorEmail: string | null } | null;
  sqsOk: boolean;
  onFlush: (includeGenerating: boolean) => Promise<void>;
  isFlushing: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [includeGenerating, setIncludeGenerating] = useState(false);
  const nowSeconds = useNowSeconds();

  // The countdown is driven off the audit record, so it survives a reload and
  // covers a purge someone else just made.
  const lastFlushSeconds =
    lastFlush === null ? null : Math.floor(djs(lastFlush.at).valueOf() / 1000);
  const purgeAvailableIn =
    lastFlushSeconds === null
      ? 0
      : Math.max(0, lastFlushSeconds + PURGE_WINDOW_SECONDS - nowSeconds);

  const nothingToFlush = queued === 0 && generating === 0 && (sqsVisible ?? 0) === 0;
  // Rendered as the disabled button's tooltip: it is a reason the button is
  // off, not page copy.
  const disabledReason = !sqsOk ? (
    <Trans>Queue status unavailable; flushing is disabled until SQS responds.</Trans>
  ) : nothingToFlush ? (
    <Trans>There is nothing to flush.</Trans>
  ) : purgeAvailableIn > 0 ? (
    <Trans>SQS allows one purge per minute.</Trans>
  ) : null;

  const flushButton = (
    <Button
      size="sm"
      variant="outline"
      colorScheme="red"
      leftIcon={<Trash2 size={iconSize.sm} />}
      isDisabled={disabledReason !== null || isFlushing}
      data-testid={testIds.flushButton}
      onClick={() => {
        setIncludeGenerating(false);
        setDialogOpen(true);
      }}
    >
      {purgeAvailableIn > 0 ? (
        <Trans>Flush queue… (available in {purgeAvailableIn}s)</Trans>
      ) : (
        <Trans>Flush queue…</Trans>
      )}
    </Button>
  );

  return (
    <>
      <Box borderWidth="1px" borderColor="border.default" borderRadius="md" px={4} py={4}>
        <Text fontSize="lg" fontWeight="semibold">
          <Trans>Danger zone</Trans>
        </Text>
        <Text fontSize="sm" color="fg.muted" mb={3}>
          <Trans>
            Flush the queue: purges every SQS message and cancels all queued rows, across every
            course and language.
          </Trans>
        </Text>
        <Stack spacing={2} align="flex-start">
          {disabledReason === null ? (
            flushButton
          ) : (
            <Tooltip label={disabledReason} shouldWrapChildren>
              {flushButton}
            </Tooltip>
          )}
          {lastFlush === null ? null : (
            <Text fontSize="xs" color="fg.muted">
              <Trans>
                Last flush: {djs(lastFlush.at).format('D MMM, h:mma')}
                {lastFlush.actorEmail === null ? '' : ` by ${lastFlush.actorEmail}`}
              </Trans>
            </Text>
          )}
        </Stack>
      </Box>

      {dialogOpen ? (
        <ConfirmationDialog
          isOpen
          onClose={() => setDialogOpen(false)}
          title={<Trans>Flush the entire translation queue?</Trans>}
          data-testid={testIds.flushDialog}
          body={
            <Box>
              <Text mb={2}>
                <Trans>
                  This affects every course and every language, not just what is on screen.
                </Trans>
              </Text>
              <UnorderedList spacing={1} mb={3}>
                <ListItem>
                  <Trans>Cancels {queued} queued rows so course pages stop waiting</Trans>
                </ListItem>
                <ListItem>
                  {sqsVisible === null ? (
                    <Trans>
                      Purges all SQS messages (can't be undone; one purge allowed per minute)
                    </Trans>
                  ) : (
                    <Trans>
                      Purges ~{sqsVisible} SQS messages (can't be undone; one purge allowed per
                      minute)
                    </Trans>
                  )}
                </ListItem>
              </UnorderedList>
              <Checkbox
                isChecked={includeGenerating}
                onChange={(event) => setIncludeGenerating(event.target.checked)}
                isDisabled={generating === 0}
              >
                <Text fontSize="sm">
                  <Trans>
                    Also cancel the {generating} in-progress translations. Their results will be
                    discarded.
                  </Trans>
                </Text>
              </Checkbox>
            </Box>
          }
          confirmLabel={<Trans>Flush queue</Trans>}
          cancelLabel={<Trans>Go back</Trans>}
          variant="destructive"
          isLoading={isFlushing}
          onConfirm={() => {
            void onFlush(includeGenerating).then(() => setDialogOpen(false));
          }}
        />
      ) : null}
    </>
  );
}
